import React from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Anchor,
  Button,
  Group,
  Loader,
  Stack,
  Text,
} from "@mantine/core";
import { IconBriefcase, IconInfoCircle } from "@tabler/icons-react";
import type { SalonService } from "../../../types/salon";
import type {
  StaffServiceAssignment,
  StaffServiceAssignments,
} from "../../../types/staffForm";
import { MenuServiceRow } from "./MenuServiceRow";
import {
  assignmentFromService,
  groupServicesByType,
} from "./staffServiceUtils";
import styles from "./StaffForm.module.css";

interface MenuPanelProps {
  /** null/undefined while the salon's services are still loading. */
  services: SalonService[] | null | undefined;
  /** The services couldn't be loaded; offer a retry instead of the spinner. */
  loadFailed: boolean;
  onRetryLoad: () => void;
  assignments: StaffServiceAssignments;
  onAssignmentChange: (serviceId: string, next: StaffServiceAssignment) => void;
  /** Why the menu is read-only, or null when it's editable. */
  readOnlyReason: string | null;
  hasBookableCalendar: boolean;
  onGoToHours: () => void;
  /** Offer a link to Settings → Services from the empty state. */
  showServicesLink: boolean;
  isMobile: boolean;
}

/** Menu: which services this staff member performs, with their own settings. */
export function MenuPanel({
  services,
  loadFailed,
  onRetryLoad,
  assignments,
  onAssignmentChange,
  readOnlyReason,
  hasBookableCalendar,
  onGoToHours,
  showServicesLink,
  isMobile,
}: MenuPanelProps) {
  if (!services && loadFailed) {
    return (
      <Alert color="red" variant="light" icon={<IconInfoCircle size={18} />}>
        <Stack gap="xs" align="flex-start">
          <Text size="sm">We couldn't load your services.</Text>
          <Button
            variant="subtle"
            color="teal"
            size="xs"
            px={0}
            onClick={onRetryLoad}
          >
            Try again
          </Button>
        </Stack>
      </Alert>
    );
  }

  if (!services) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" color="teal" aria-label="Loading services" />
      </Group>
    );
  }

  if (services.length === 0) {
    return (
      <Stack gap="xs" align="center" className={styles.emptyState}>
        <IconBriefcase size={48} stroke={1.5} color="var(--brand-black)" />
        <Text size="md" fw={500} ta="center">
          You don't have any services yet.
        </Text>
        <Text size="sm" c="dimmed" ta="center">
          You will be able to add services later.
        </Text>
        {showServicesLink && (
          <Anchor component={Link} to="/admin/settings/services" c="teal">
            Go to Services
          </Anchor>
        )}
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {!hasBookableCalendar && (
        <Alert color="gray" variant="light" icon={<IconInfoCircle size={18} />}>
          <Stack gap="xs" align="flex-start">
            <Text size="sm">
              This staff member is hidden from the calendar, so these services
              won't be bookable until Has a bookable calendar is on.
            </Text>
            <Button
              variant="subtle"
              color="teal"
              size="xs"
              px={0}
              onClick={onGoToHours}
            >
              Go to Hours
            </Button>
          </Stack>
        </Alert>
      )}

      {readOnlyReason && (
        <Alert color="gray" variant="light" icon={<IconInfoCircle size={18} />}>
          {readOnlyReason}
        </Alert>
      )}

      {groupServicesByType(services).map((group) => (
        <Stack key={group.type} gap="sm">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">
            {group.type}
          </Text>
          {group.services.map((service) => (
            <MenuServiceRow
              key={service.id}
              service={service}
              assignment={
                assignments[service.id] ?? assignmentFromService(service)
              }
              onChange={(next) => onAssignmentChange(service.id, next)}
              disabled={!!readOnlyReason}
              isMobile={isMobile}
            />
          ))}
        </Stack>
      ))}
    </Stack>
  );
}
